import { useMutation } from "react-query";
import { useMyContext } from "../context";
import apiClient from "../utils/apiClient";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  getFCMToken,
  requestNotificationPermission,
  getCurrentToken,
  shouldRefreshToken,
  debugFCMToken,
  getTokenStatus,
  initializeFCM,
  sendTokenToServer as sendTokenToServerFirebase,
} from "../firebase/firebase";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { decodeAndValidateToken } from "../utils/tokenUtils";

const useAuth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    number,
    password,
    saveToken, // Make sure this is in your context
    checkTokenValidity,
    setUser,
    isAuthenticated,
    setIsAuthenticated,
  } = useMyContext();

  const hasCheckedToken = useRef(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [isFcmInitialized, setIsFcmInitialized] = useState(false);
  const [fcmLoading, setFcmLoading] = useState(false);
  const [fcmError, setFcmError] = useState(null);
  const loginInProgress = useRef(false);

  const getFCMStatus = useCallback(
    () => ({
      token: fcmToken,
      permission: Notification.permission,
      error: fcmError,
      loading: fcmLoading,
      initialized: isFcmInitialized,
      hasPreLoginToken: !!localStorage.getItem("pre_login_fcm_token"),
    }),
    [fcmToken, fcmError, fcmLoading, isFcmInitialized]
  );

  // SIMPLIFIED: Remove complex notification flags
  const notificationRequested = useRef(false);

  // ✅ SIMPLIFIED FCM INITIALIZATION
  useEffect(() => {
    const initializeFCMToken = async () => {
      try {
        console.log("🔄 Checking for existing FCM token...");
        setFcmLoading(true);

        // 1. Check if we already have a valid token
        const existingToken = getCurrentToken();
        const needsRefresh = shouldRefreshToken();

        if (existingToken && !needsRefresh) {
          console.log("✅ Using existing valid FCM token");
          setFcmToken(existingToken);
          setIsFcmInitialized(true);
          setFcmLoading(false);
          return;
        }

        // 2. If no token or needs refresh, check permission
        const permission = Notification.permission;

        if (permission === "granted") {
          // Try to get new token quietly (no permission request)
          console.log("🔑 Permission granted, getting token silently...");
          try {
            const token = await getFCMToken(false); // false = don't request permission
            if (token) {
              setFcmToken(token);
              console.log("✅ Got FCM token silently");
            }
          } catch (error) {
            console.log("⚠️ Couldn't get token silently:", error.message);
          }
        } else if (permission === "default") {
          // Permission not decided yet - don't request yet
          console.log("⏳ Notification permission not decided yet");
        } else {
          // Permission denied
          console.log("🔕 Notification permission denied");
        }

        setIsFcmInitialized(true);
        setFcmLoading(false);
      } catch (error) {
        console.error("❌ Error initializing FCM:", error);
        setIsFcmInitialized(true);
        setFcmLoading(false);
        setFcmError(error.message);
      }
    };

    // Initialize immediately when hook loads
    initializeFCMToken();
  }, []);

  // ✅ SIMPLIFIED: Check token validity
  useEffect(() => {
    if (hasCheckedToken.current || loginInProgress.current || isAuthenticated)
      return;

    const verifyToken = async () => {
      try {
        console.log("🔄 Checking existing token...");
        const isValid = await checkTokenValidity();

        if (isValid) {
          console.log("✅ Token valid, redirecting to Dashboard");
          hasCheckedToken.current = true;
          navigate("/dashboard", { replace: true });
        } else {
          console.log("❌ Token invalid or expired");
        }
      } catch (error) {
        console.error("❌ Error checking token:", error);
      }
    };

    verifyToken();
  }, [checkTokenValidity, navigate, isAuthenticated]);

  // ✅ CORRECTED: Send token to server (keeping original logic)
  const sendTokenToServer = useCallback(async (fcmToken) => {
    try {
      const userToken = sessionStorage.getItem("ecoToken");
      const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");

      if (!userToken || !userData.id) {
        console.log("⚠️ Not logged in yet, will send token later");
        // Store token to send later
        localStorage.setItem("pending_fcm_token", fcmToken);
        return false;
      }

      console.log("📤 Sending FCM token to server...");
      const response = await apiClient.post(
        "/save-fcm-token",
        {
          fcmToken: fcmToken,
          userId: userData.id,
          deviceId: `WEB_${navigator.userAgent.substring(0, 50)}_${Date.now()}`,
          platform: "web",
          browser: navigator.userAgent,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
          // isAuthorized: true (افتراضي) - apiClient يضيف التوكن تلقائياً
        }
      );

      if (response.data.success) {
        console.log("✅ Token sent to server successfully");
        localStorage.removeItem("pending_fcm_token");
        return true;
      } else {
        console.warn("⚠️ Server response not successful:", response.data);

        // Try alternative endpoint if main one fails
        try {
          const altResponse = await sendTokenToServerFirebase(
            fcmToken,
            userData.id
          );
          console.log(
            "🔧 Alternative send result:",
            altResponse ? "✅ Success" : "❌ Failed"
          );
          return altResponse;
        } catch (altError) {
          console.error("❌ Alternative send also failed:", altError);
          return false;
        }
      }
    } catch (error) {
      console.error("❌ Error sending token to server:", error);
      return false;
    }
  }, []);

  // ✅ SIMPLIFIED: Get or request FCM token
  const getOrRequestFCMToken = useCallback(
    async (forceRequest = false) => {
      try {
        setFcmLoading(true);
        setFcmError(null);

        console.log("🔄 Getting FCM token...");

        // Check for existing valid token first
        const existingToken = getCurrentToken();
        const needsRefresh = shouldRefreshToken();

        // If token exists and doesn't need refresh, use it
        if (existingToken && !needsRefresh && !forceRequest) {
          console.log("✅ Using existing valid token");
          setFcmToken(existingToken);
          setFcmLoading(false);
          return existingToken;
        }

        // Check permission status
        const permission = Notification.permission;

        let token = null;

        if (permission === "default") {
          // Ask user for permission
          console.log("🔔 Asking for notification permission...");
          const granted = await requestNotificationPermission();

          if (granted) {
            token = await getFCMToken(false); // Get token after permission
          }
        } else if (permission === "granted") {
          // Permission already granted, get token
          token = await getFCMToken(false);
        } else {
          // Permission denied
          console.warn("🔕 Notification permission denied by user");
          setFcmError("Notifications blocked in browser settings");
        }

        if (token) {
          console.log("✅ Got FCM token");
          setFcmToken(token);

          // Send to server if user is logged in
          const userToken = sessionStorage.getItem("ecoToken");
          if (userToken) {
            await sendTokenToServer(token);
          }
        }

        setFcmLoading(false);
        return token;
      } catch (error) {
        console.error("❌ Error getting FCM token:", error);
        setFcmError(error.message);
        setFcmLoading(false);
        return null;
      }
    },
    [sendTokenToServer]
  );

  // ✅ SIMPLIFIED: Show notification permission dialog
  const showNotificationPermissionDialog = useCallback(
    async (forceShow = false) => {
      try {
        console.log("🔔 Showing notification permission dialog...");

        // Check current permission
        const permission = Notification.permission;

        if (permission !== "default") {
          console.log(
            `Permission is already ${permission}, not showing dialog`
          );
          return null;
        }

        // Check if we've asked recently (unless forced)
        const lastAsked = localStorage.getItem("notification_last_asked");
        const now = Date.now();
        const hours24 = 24 * 60 * 60 * 1000;

        if (!forceShow && lastAsked && now - parseInt(lastAsked) <= hours24) {
          console.log("⏰ Asked recently, not showing dialog");
          return null;
        }

        // Show the dialog
        const result = await Swal.fire({
          title: "Enable Notifications",
          html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Get real-time updates for:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px; font-size: 14px;">
              <li>📱 New trip requests & assignments</li>
              <li>🚨 Incident & emergency alerts</li>
              <li>📋 Important announcements</li>
              <li>🔔 Booking status changes</li>
            </ul>
            <p style="margin-top: 15px; font-size: 13px; color: #666; border-top: 1px solid #eee; padding-top: 10px;">
              🔒 We respect your privacy. Notifications only for essential updates.
            </p>
          </div>
        `,
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Yes, enable notifications",
          cancelButtonText: "Maybe later",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#6c757d",
          customClass: {
            popup: "notification-permission-popup",
            confirmButton: "btn btn-primary btn-lg",
            cancelButton: "btn btn-secondary btn-lg",
          },
          buttonsStyling: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        // Store when we asked
        localStorage.setItem("notification_last_asked", now.toString());
        notificationRequested.current = true;

        if (result.isConfirmed) {
          console.log("✅ User agreed to enable notifications");
          const granted = await requestNotificationPermission();

          if (granted) {
            console.log("🔑 Permission granted, getting token...");
            const token = await getFCMToken(false);

            if (token) {
              setFcmToken(token);

              // Send to server if user is logged in
              const userToken = sessionStorage.getItem("ecoToken");
              if (userToken) {
                await sendTokenToServer(token);
              }

              return token;
            }
          }
        } else {
          console.log("⏰ User postponed notification permission");
        }

        return null;
      } catch (error) {
        console.error("❌ Error showing notification dialog:", error);
        return null;
      }
    },
    [sendTokenToServer]
  );

  // ✅ CORRECTED: Login mutation WITH REFRESH TOKEN
  const adminLogin = useMutation({
    mutationFn: async () => {
      console.log("🚀 Starting login process...");
      loginInProgress.current = true;

      // Backend requires device.fcm_token, but browser push is optional for dashboard login.
      const deviceId = `WEB_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      let fcmTokenToUse =
        localStorage.getItem("pre_login_fcm_token") ||
        fcmToken ||
        getCurrentToken() ||
        null;

      const permission =
        typeof Notification !== "undefined"
          ? Notification.permission
          : "denied";

      if (!fcmTokenToUse && permission !== "denied") {
        try {
          console.log("🔄 Requesting FCM token before login...");
          fcmTokenToUse = await getOrRequestFCMToken(false);
        } catch (tokenError) {
          console.warn("⚠️ FCM token request failed:", tokenError);
        }
      }

      if (!fcmTokenToUse) {
        fcmTokenToUse = `web_push_unavailable_${deviceId}`;
        console.log(
          "⚠️ No browser FCM token; sending placeholder so login can proceed",
        );
      } else {
        console.log(
          "📤 Using FCM token:",
          fcmTokenToUse.substring(0, 30) + "...",
        );
        localStorage.removeItem("pre_login_fcm_token");
        localStorage.setItem("fcmToken", fcmTokenToUse);
      }

      const loginForm = new FormData();
      loginForm.append("phone_number", number);
      loginForm.append("password", password);
      loginForm.append("device[device_id]", deviceId);
      loginForm.append("device[platform]", "WEB");
      loginForm.append("device[fcm_token]", fcmTokenToUse);

      const res = await apiClient.post(
        "/auth/login",
        loginForm,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000,
          isAuthorized: false, // تسجيل الدخول لا يحتاج توكن
        }
      );

      console.log("✅ Login response received");
      return res.data;
    },
    onSuccess: async (data) => {
      try {
        console.log("🎉 Login successful");

        // Extract tokens from response
        let token = null;
        let refreshToken = null;
        let extractedUserData = null;

        // Try to extract token from various structures
        if (data.token) {
          token = data.token;
        } else if (data.access_token) {
          token = data.access_token;
        } else if (data.data?.token) {
          token = data.data.token;
        } else if (data.data?.access_token) {
          token = data.data.access_token;
        } else if (data.data?.data?.token) {
          token = data.data.data.token;
        }

        // Try to extract refresh token
        if (data.refresh_token) {
          refreshToken = data.refresh_token;
        } else if (data.data?.refresh_token) {
          refreshToken = data.data.refresh_token;
        } else if (data.refreshToken) {
          refreshToken = data.refreshToken;
        } else if (data.data?.refreshToken) {
          refreshToken = data.data.refreshToken;
        }

        // Extract user data
        // Try multiple response formats
        if (data.user) {
          extractedUserData = data.user;
        } else if (data.data && data.data.user) {
          extractedUserData = data.data.user;
        } else if (data.data && typeof data.data === "object") {
          extractedUserData = data.data;
        }

        console.log("🔑 Extracted data:", {
          hasToken: !!token,
          hasRefreshToken: !!refreshToken,
          hasUserData: !!extractedUserData,
        });

        if (!token) {
          console.error("❌ Token not found in response");
          throw new Error("Failed to get token from server");
        }

        // Validate token if possible
        try {
          const { isValid } = decodeAndValidateToken(token);
          console.log(
            "🔍 Token validation:",
            isValid ? "✅ Valid" : "⚠️ Invalid or can't validate"
          );
        } catch (validationError) {
          console.warn(
            "⚠️ Token validation error (continuing anyway):",
            validationError.message
          );
        }

        // Save tokens and user data to sessionStorage
        sessionStorage.setItem("ecoToken", token);
        console.log("✅ Saved access token to sessionStorage");

        if (refreshToken) {
          sessionStorage.setItem("ecoRefreshToken", refreshToken);
          console.log("✅ Saved refresh token to sessionStorage");
        } else {
          console.warn("⚠️ No refresh token in response");
          sessionStorage.removeItem("ecoRefreshToken");
        }

        // Ensure user data has minimum structure
        const finalUserData = extractedUserData
          ? {
              id:
                extractedUserData.id || extractedUserData.user_id || "unknown",
              phone_number: extractedUserData.phone_number || number,
              name: extractedUserData.name || extractedUserData.full_name || "",
              email: extractedUserData.email || "",
              role:
                extractedUserData.role || extractedUserData.user_type || "user",
              ...extractedUserData,
            }
          : {
              id: "unknown",
              phone_number: number,
              name: "",
              email: "",
              role: "user",
            };

        sessionStorage.setItem("userData", JSON.stringify(finalUserData));
        console.log("✅ Saved user data to sessionStorage");

        // Set authentication state in context
        setIsAuthenticated(true);
        if (setUser) setUser(finalUserData);

        // Also call saveToken for consistency if it exists in context
        if (saveToken) {
          saveToken(token, finalUserData, refreshToken);
          console.log("✅ Called saveToken from context");
        }

        // Set flags
        hasCheckedToken.current = true;
        loginInProgress.current = false;

        // Show success message
        await Swal.fire({
          icon: "success",
          title: t("login.success"),
          showConfirmButton: false,
          timer: 1500,
        });

        console.log("🚀 Redirecting to Dashboard...");
        navigate("/dashboard", {
          replace: true,
          state: {
            fromLogin: true,
            timestamp: Date.now(),
          },
        });
      } catch (error) {
        console.error("❌ Error processing login:", error);
        loginInProgress.current = false;

        // Clear any partial data on error
        sessionStorage.removeItem("ecoToken");
        sessionStorage.removeItem("ecoRefreshToken");
        sessionStorage.removeItem("userData");
        setIsAuthenticated(false);

        Swal.fire({
          icon: "error",
          title: t("login.error"),
          text: error.message || t("login.errorProcessing"),
          confirmButtonText: t("login.ok"),
        });
      }
    },
    onError: (error) => {
      console.error("❌ Login error:", error);
      loginInProgress.current = false;

      let errorMessage = t("login.errorGeneric");

      // معالجة أخطاء axios (apiClient هو axios instance)
      if (error.response) {
        const fieldErrors = error.response.data?.errors;
        const fcmError =
          fieldErrors?.["device.fcm_token"] || fieldErrors?.fcm_token;
        errorMessage =
          (typeof fcmError === "string" && fcmError) ||
          (Array.isArray(fcmError) && fcmError[0]) ||
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.errors?.message ||
          `Error ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        // الطلب أُرسل لكن لم يصل رد
        errorMessage = t("login.cannotReach");
      } else {
        // خطأ في إعداد الطلب
        errorMessage = error.message || t("login.errorUnknown");
      }

      Swal.fire({
        icon: "error",
        title: t("login.failed"),
        text: errorMessage,
        confirmButtonText: t("login.ok"),
      });
    },
  });

  // Handle login
  const handleLogin = useCallback(async () => {
    // Check if we have valid session using JWT decode
    const token = sessionStorage.getItem("ecoToken");
    const refreshToken = sessionStorage.getItem("ecoRefreshToken");

    if (token) {
      try {
        const { isValid } = decodeAndValidateToken(token);

        if (isValid) {
          // Valid session exists
          const result = await Swal.fire({
            title: t("login.activeSession"),
            html: `
              <div style="text-align: start; padding: 10px;">
                <p>${t("login.activeSessionBody")}</p>
                <p style="font-size: 14px; color: #666; margin-top: 10px;">
                  ${t("login.activeSessionAsk")}
                </p>
              </div>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t("login.continueSession"),
            cancelButtonText: t("login.newLogin"),
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#6c757d",
          });

          if (result.isConfirmed) {
            console.log("✅ Continuing existing session");
            hasCheckedToken.current = true;
            navigate("/dashboard", { replace: true });
            return;
          } else {
            // Clear session and login again
            console.log("🔄 Starting new login session");
            sessionStorage.removeItem("ecoToken");
            sessionStorage.removeItem("ecoRefreshToken");
            sessionStorage.removeItem("userData");
            setIsAuthenticated(false);
          }
        } else if (refreshToken) {
          // Token is invalid but we have refresh token
          console.log("🔄 Token expired but refresh token available");
          // Let the user login fresh
        }
      } catch (error) {
        console.warn("⚠️ Error checking existing token:", error);
        // Token might be invalid, proceed with new login
      }
    }

    // No valid session, proceed with login
    console.log("🚀 Starting new login process");
    adminLogin.mutate();
  }, [adminLogin, navigate, setIsAuthenticated, t]);

  // Force refresh FCM token
  const refreshFCMToken = useCallback(async () => {
    try {
      setFcmLoading(true);
      console.log("🔄 Force refreshing FCM token...");

      const token = await getOrRequestFCMToken(true); // Force request

      if (token) {
        Swal.fire({
          icon: "success",
          title: t("login.tokenRefreshed"),
          text: t("login.tokenUpdated"),
          timer: 2000,
          showConfirmButton: false,
        });
      }

      setFcmLoading(false);
      return token;
    } catch (error) {
      console.error("❌ Error refreshing FCM token:", error);
      setFcmLoading(false);
      return null;
    }
  }, [getOrRequestFCMToken, t]);

  // Manual notification permission request
  const requestNotificationPermissionManually = useCallback(async () => {
    try {
      setFcmLoading(true);
      setFcmError(null);

      console.log("🔔 Manual notification permission request");
      const token = await getOrRequestFCMToken(true); // Force request

      if (token) {
        Swal.fire({
          icon: "success",
          title: `✅ ${t("login.notificationsEnabled")}`,
          text: t("login.notificationsEnabledBody"),
          timer: 2000,
          showConfirmButton: false,
        });
      } else if (!fcmError) {
        Swal.fire({
          icon: "info",
          title: t("login.notificationStatus"),
          html: `
            <div style="text-align: start; padding: 10px;">
              <p>${t("login.notificationCurrent")} <strong>${
                Notification.permission
              }</strong></p>
              ${
                Notification.permission === "denied"
                  ? `<p style="color: #dc3545; margin-top: 10px;">${t(
                      "login.notificationDeniedHint"
                    )}</p>`
                  : ""
              }
            </div>
          `,
          confirmButtonText: t("login.ok"),
        });
      }

      setFcmLoading(false);
    } catch (error) {
      console.error("❌ Error requesting notification permission:", error);
      setFcmError(error.message);
      setFcmLoading(false);

      Swal.fire({
        icon: "error",
        title: t("login.requestFailed"),
        text: t("login.requestPermissionFailed"),
        confirmButtonText: t("login.ok"),
      });
    }
  }, [fcmError, getOrRequestFCMToken, t]);

  return {
    authLoading: adminLogin.isPending || adminLogin.isLoading || fcmLoading,
    handleLogin,
    error: adminLogin.error,
    fcmToken,
    fcmError,
    isFcmInitialized,
    fcmLoading,
    requestNotificationPermission: requestNotificationPermissionManually,
    refreshFCMToken,
    getOrRequestFCMToken,
    showNotificationPermissionDialog,
    getFCMStatus,
    loginMutation: adminLogin,
  };
};

export default useAuth;
